/**
 * Detects whether the current device is a desktop.
 * Used to decide if the button should be enhanced with phone input
 * or act as a simple link.
 *
 * Detection priority:
 * 1. Screen size (< 768px = mobile)
 * 2. Pointer/hover capabilities
 * 3. Touch + screen size heuristics
 * 4. User agent fallback
 */
export function isDesktop(): boolean {
  const screenWidth = window.screen.width;

  // Small screen = mobile regardless of other capabilities
  if (screenWidth < 768) {
    return false;
  }

  const hasTouch =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  const hasCoarsePointer =
    window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const hasFinePointer =
    window.matchMedia?.("(pointer: fine)").matches ?? false;
  const hasHover =
    window.matchMedia?.("(hover: hover)").matches ?? false;

  // Desktop: has hover + fine pointer + large screen
  if (hasHover && hasFinePointer && screenWidth >= 1024) {
    return true;
  }

  // Mobile/Tablet: touch without hover, coarse pointer, or small screen
  if ((hasTouch && !hasHover) || hasCoarsePointer || screenWidth < 1024) {
    return false;
  }

  // User agent fallback
  const userAgent = (
    navigator.userAgent ||
    navigator.vendor ||
    ""
  ).toLowerCase();
  const isMobileUA =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(
      userAgent
    );

  if (isMobileUA) {
    return false;
  }

  // Default to desktop for large screens
  return true;
}
